import React, { useCallback, useEffect, useRef, useState } from "react";
import IntakeFormFields from "../IntakeFormFields/IntakeFormFields";
import {
  Button,
  Link,
  LoadingSpinner,
  Notification,
  NotificationType,
  Tooltip,
} from "pepsico-ds";
import Backdrop from "@mui/material/Backdrop";
import { validateField } from "../../utils/validators";
import {
  documentclassification,
  IntakefieldNameMapping,
  IntakeMadatoryFields,
  sowfileFormat,
} from "../../utils/fieldConstants";
import { useDispatch, useSelector } from "react-redux";
import { CloseOutRadioActions } from "../../redux/closeoutslice/closeOutRadioSlice";
import { setIntakeProjectDetails } from "../../redux/actions/projectActions";
import DocumentUploadInput from "../DocumentUploadInput/DocumentUploadInput";
import ModalPopUp from "../../shared/modalPopUp";
import {
  CloseOutActions,
  saveIntakeFiles,
} from "../../redux/closeoutslice/closeoutSlice";
import UseUploadInChunks from "../../shared/Hooks/FileUploadInChunks";
import { saveIntakeForm } from "../../services/intakeFormService";
import { AppDispatch } from "../../redux/store";
import { unwrapResult } from "@reduxjs/toolkit";
import AppToaster from "../../shared/appToaster";
import urls from "../../configmap/config.json";
const RequestForm = () => {
  const [IntakeFormData, setIntakeFormData] = useState<{
    [key: string]: any;
  }>({});
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
  const [validInputError, setValidInputError] = useState<{
    [key: string]: string | null;
  }>({});
  const [formattedProjectId, setFormattedProjectId] = useState<any>("");
  const [isEditMode, setIsEditMode] = useState<any>(false);
  const [emailErrors, setEmailErrors] = useState<{
    [key: string]: string | null;
  }>({});
  const [focusedFields, setFocusedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [isSaveDisabled, setIsSaveDisabled] = useState<any>(true);
  const [isAutoFillDisabled, setIsAutoFillDisabled] = useState(true);
  const {
    historicalFormData,
    isUploading,
    files,
    fileClassificationSelection,
    uploadedFilesSuccess,
    uploadedFiles,
    preuploadedFilesSuccess,
  } = useSelector((state: any) => state.closeOut);

  const [Loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { gpid } = useSelector((state: any) => state.auth);
  const { fileUploadInChuncks } = UseUploadInChunks();
  const [ismodalOpen, setisModalOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const uploadRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (historicalFormData.data) {
      setIntakeFormData((prevState) => {
        return Object.fromEntries(
          Object.entries(historicalFormData.data).map(([key, newValue]) => [
            key,
            newValue !== null ? newValue : prevState[key],
          ])
        );
      });
    }
  }, [historicalFormData]);
  useEffect(() => {
    const newErrors: { [key: string]: string | null } = {};
    IntakeMadatoryFields.forEach((field: any) => {
      if (focusedFields[field]) {
        // Only check if field has been focused
        const displayName = IntakefieldNameMapping[field] || field;
        newErrors[field] = IntakeFormData[field]
          ? null
          : `${displayName} is a mandatory field.`;
      }
    });
    setErrors(newErrors);
  }, [IntakeFormData, focusedFields]);
  useEffect(() => {
    // Only run the validation if the form data is fully populated
    // if (!formDataIsReady.current) return;

    const timer = setTimeout(() => {
      // Check for empty fields, using fallback for undefined/null
      const hasEmptyFields = IntakeMadatoryFields.some((field) => {
        const value = IntakeFormData[field] || ""; // Fallback to empty string if field is undefined or null
        return value.trim() === ""; // If the value is an empty string or only spaces
      });

      const hasFiles = files.length > 0;

      const hasErrors = Object.values(validInputError).some(
        (error) => error !== null
      );
      // Ensure every file has a valid classification ('confidential' or 'restricted')
      const allFilesClassifiedCorrectly = files.every((file: any) => {
        const is_doc_classification =
          file.document_classification ===
            documentclassification.confidential ||
          file.document_classification === documentclassification.restricted ||
          file.document_classification === documentclassification.internal;
        const is_file_classification_selection =
          fileClassificationSelection[file.name] &&
          (fileClassificationSelection[file.name] ===
            documentclassification.confidentialCatergory ||
            fileClassificationSelection[file.name] ===
              documentclassification.restrictedCategory ||
            fileClassificationSelection[file.name] ===
              documentclassification.internalCategory);

        return is_doc_classification || is_file_classification_selection;
      });

      const hasValidClassification = files.some((file: any) => {
        const isDocumentClassificationValid =
          file.document_classification ===
            documentclassification.confidential ||
          file.document_classification === documentclassification.internal;

        const isFileClassificationSelectionValid =
          fileClassificationSelection[file.name] &&
          (fileClassificationSelection[file.name] ===
            documentclassification.confidentialCatergory ||
            fileClassificationSelection[file.name] ===
              documentclassification.internalCategory);
        return (
          isDocumentClassificationValid || isFileClassificationSelectionValid
        );
      });
      // Check if all files are already uploaded
      const allFilesUploaded = files.every((file: any) =>
        uploadedFilesSuccess.includes(file.name)
      );
      const allRestrictedFilesNotUploaded = files
        .filter((file: any) => !uploadedFilesSuccess.includes(file.name))
        .every(
          (file: any) =>
            file.document_classification ===
              documentclassification.restricted ||
            fileClassificationSelection[file.name] ===
              documentclassification.restrictedCategory
        );
      const hasValidFileType = files
        .filter((file: any) => !uploadedFilesSuccess.includes(file.name))
        .some((file: any) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          return sowfileFormat.includes(fileExtension);
        });
        const eligibleFiles = files
          .filter((file: any) => !uploadedFilesSuccess.includes(file.name))
          .filter((file: any) => {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            const isRestricted =
              file.document_classification ===
                documentclassification.restricted ||
              fileClassificationSelection[file.name] ===
                documentclassification.restrictedCategory;
            const isValidFormat = sowfileFormat.includes(fileExtension);
            return !isRestricted && isValidFormat;
          });
      setIsAutoFillDisabled(
        !hasFiles ||
          eligibleFiles.length === 0 ||
          !allFilesClassifiedCorrectly ||
          !hasValidClassification ||
          allFilesUploaded ||
          allRestrictedFilesNotUploaded ||
          !hasValidFileType
      );
      // Update the button state (enabled/disabled)
      setIsSaveDisabled(
        hasEmptyFields || !hasFiles || hasErrors || !allFilesClassifiedCorrectly
      );
    }, 200); // Delay the check by 200ms (this is the debounce)

    return () => clearTimeout(timer); // Clean up timeout on dependency change
  }, [
    IntakeFormData,
    validInputError,
    emailErrors,
    files,
    fileClassificationSelection,
    uploadedFilesSuccess,
  ]);

  const historicalHandleChange = useCallback(
    (value: any, key: string) => {
      // Immediate field update
      setIntakeFormData((prev: any) => ({
        ...prev,
        [key]: value,
      }));

      setValidInputError((prevErrors) => ({
        ...prevErrors,
        [key]: validateField(key, value),
      }));
      if (value) {
        // Clear other errors if the field is valid (for other fields, not just email)
        setErrors((prev) => ({ ...prev, [key]: null }));
      }
    },
    [IntakeFormData]
  );

  const handleFocus = (key: string) => {
    setFocusedFields((prev) => ({ ...prev, [key]: true }));
  };

  const handleBlur = (key: string) => {
    setFocusedFields((prev) => ({ ...prev, [key]: false }));
    const displayName = IntakefieldNameMapping[key] || key;
    if (!IntakeFormData[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: `${displayName} is a mandatory field.`,
      }));
    }
  };

  const historicalHandleFileChange = (newFiles: File[]) => {
    //   setHistoricalFiles(newFiles);
  };
  const handleCloseHistoricalDialog = () => {
    dispatch(CloseOutRadioActions.setisfilUpload(false));
    dispatch(CloseOutRadioActions.setRequestForm(false));
    dispatch(CloseOutActions.resetAll());
    dispatch(CloseOutActions.setIntakeData({}));
    dispatch(CloseOutActions.resetPreUpload());
  };
  const submitIntakeRequest = async () => {
    setOpenModal(false);
    dispatch(CloseOutActions.setIsuploading(true));
    dispatch(CloseOutActions.setDisplayProgress(true));
    setLoading(true);
    const filesToUpload = files.filter((file: any) => {
      return !uploadedFilesSuccess.includes(file.name) && file.size > urls.minFileSize;
    });
    if (filesToUpload.length === 0 && preuploadedFilesSuccess.length === 0) {
      dispatch(
        CloseOutActions.setErrorMessage(
          "All selected files have already been uploaded."
        )
      );
      dispatch(CloseOutActions.setIsuploading(false));
      setLoading(false);
      return;
    } else if (
      filesToUpload.length === 0 &&
      preuploadedFilesSuccess.length !== 0
    ) {
      const updatedPreFiles = preuploadedFilesSuccess.map(
        ({ name, ...rest }: { name: string; [key: string]: any }) => rest
      );
      handleSave(updatedPreFiles, true);
      return;
    }
    let localConstructedFiles: any[] = [];
    for (const file of filesToUpload) {
      try {
        const classification = fileClassificationSelection[file.name];
        // Upload the file in chunks and store the constructed files
        await fileUploadInChuncks(file, localConstructedFiles, classification);
        // Update Redux state with the uploaded file's name
        dispatch(
          CloseOutActions.setUploadedFiles([...uploadedFiles, file.name])
        );
        dispatch(
          CloseOutActions.setUploadedfileSuccess([
            ...uploadedFilesSuccess,
            file.name,
          ])
        );
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
      }
    }
    // After all files are uploaded, dispatch the constructed files to the store
    dispatch(CloseOutActions.setConstructedFiles(localConstructedFiles));
    dispatch(CloseOutActions.setDisplayProgress(false));
    if (localConstructedFiles.length) {
      dispatch(CloseOutActions.setIsuploading(false));
      handleSave(localConstructedFiles, false);
    }
  };
  const handleSave = async (
    localConstructedFiles: any,
    preUploadedfile: boolean
  ) => {
    if (isSaveDisabled) {
      return;
    }
    const updatedPreFiles = preuploadedFilesSuccess.map(
      ({ name, ...rest }: { name: string; [key: string]: any }) => rest
    );
    const filesToSumbit = preUploadedfile
      ? localConstructedFiles
      : [...updatedPreFiles, ...localConstructedFiles];
    setLoading(true);
    try {
      const finalData: any = {
        ...IntakeFormData,
        formattedProjectId,
        supportingDocuments: filesToSumbit,
      };
      // Update the combined data array
      const apiBody = [
        {
          data: {
            is_new: true,
            project_code: finalData.formattedProjectId,
            RITM_number: "",
            user_id: gpid,
            project_name: finalData.project_name,
            project_brief: finalData.project_brief,
            requested_by: finalData.requestedBy,
            project_lead_name: finalData.project_lead_name,
            project_lead_email: finalData.project_lead_email,
            request_manager: finalData.manager,
            location: finalData.location,
            email: finalData.email,
            rate_value_received: finalData.rate_value_received,
            finance_lead_name: finalData.manager,
            finance_lead_email: finalData.email,
            business_stack_req_group: finalData.business_stack_req_group,
            cost_center: "", // Static value
            sector: finalData.sector,
            geo_graphic_scope: "", // Static value
            is_new_consulting_project: "",
            consulting_type: finalData.consulting_type,
            estimated_project_start_date:
              finalData.estimated_project_start_date,
            estimated_project_end_date: finalData.estimated_project_end_date,
            is_consulting_work: "",
            preferred_vendor: finalData.consulting_company,
            core_deliverables: finalData.core_deliverables,
            project_budget: "",
            is_follow_up: "",
            consulting_company: finalData.consulting_company,
            final_total_cost: finalData.final_total_cost,
            purchase_order_number: "", // Placeholder
            opportunity_cost: "", // Placeholder
            is_address_business_problem: "",
            is_internal_team_address_business_problem: "",
            is_external_scope: "",
            is_proceed_further: "",
            form_type: 1,
            updated_date: new Date().toISOString(),
          },
          attached_files: finalData.supportingDocuments,
        },
      ];
      // Make the API call
      await saveIntakeForm(apiBody);
      setOpenModal(false);
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          message: "The engagement data form has been uploaded successfully.",
          severity: "success",
        })
      );
      dispatch(CloseOutActions.resetAll());
      dispatch(CloseOutActions.setIntakeData({}));
      dispatch(CloseOutActions.resetPreUpload());
    } catch (error: any) {
    } finally {
      setLoading(false);
      handleCloseHistoricalDialog();
      dispatch(CloseOutActions.resetAll());
      dispatch(CloseOutActions.resetPreUpload());
    }
  };
  const HandleSubmit = async () => {
    setisModalOpen(false);
    dispatch(CloseOutActions.setIsuploading(true));
    dispatch(CloseOutActions.setDisplayProgress(true));
    setLoader(true);
    const filesToUpload = files.filter((file: any) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      return (
        !uploadedFilesSuccess.includes(file.name) &&
        file.size > urls.minFileSize &&
        sowfileFormat.includes(fileExtension) &&
        fileClassificationSelection[file.name] !==
          documentclassification.restrictedCategory
      );
    });
    if (filesToUpload.length === 0) {
      dispatch(
        CloseOutActions.setErrorMessage(
          "All selected files have already been uploaded."
        )
      );
      dispatch(CloseOutActions.setIsuploading(false));
      return;
    }
    let localConstructedFiles: any[] = [];
    for (const file of filesToUpload) {
      try {
        const classification = fileClassificationSelection[file.name];
        // Upload the file in chunks and store the constructed files
        await fileUploadInChuncks(file, localConstructedFiles, classification);
        // Update Redux state with the uploaded file's name
        dispatch(
          CloseOutActions.setUploadedFiles([...uploadedFiles, file.name])
        );
        dispatch(
          CloseOutActions.setUploadedfileSuccess([
            ...uploadedFilesSuccess,
            file.name,
          ])
        );
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
      }
    }
    // Filter files with document_classification !== 2
    const filteredFiles = localConstructedFiles
      .filter(
        (file: any) =>
          file.document_classification !== documentclassification.restricted
      )
      .map((file: any) => ({
        ...file,
        form_type: "web",
      }));
    // After all files are uploaded, dispatch the constructed files to the store
    dispatch(CloseOutActions.setConstructedFiles(localConstructedFiles));
    dispatch(CloseOutActions.setDisplayProgress(false));
    if (localConstructedFiles.length) {
      dispatch(CloseOutActions.setIsuploading(false));
      const PreFiles = localConstructedFiles.map((file) => ({
        ...file,
        name: file.file_name,
      }));
      if (filteredFiles.length) {
        try {
          // Call the API and handle the response
          const response = await dispatch(saveIntakeFiles(filteredFiles));
          const result = unwrapResult(response);
          if (result.code === 200 || result.code === 201) {
            dispatch(
              CloseOutRadioActions.setSnackBarMessage({
                message:
                  "The engagement data form has been auto-filled successfully.",
                severity: "success",
              })
            );
          } else {
            dispatch(
              CloseOutRadioActions.setSnackBarMessage({
                message:
                  "A problem occurred while autofilling the form. Please try to fill the form manually.",
                severity: "error",
              })
            );
          }
        } catch (err) {
          dispatch(
            CloseOutRadioActions.setSnackBarMessage({
              message:
                "A problem occurred while autofilling the form. Please try to fill the form manually.",
              severity: "error",
            })
          );
        } finally {
          dispatch(CloseOutActions.setPreUploadedfileSuccess(PreFiles));
          dispatch(CloseOutActions.setPreUploadedFilesToIntake());
          setLoader(false);
          if (formRef.current) {
            formRef.current?.scrollIntoView({ behavior: "smooth" });
          }
        }
      }
    }
  };
  const autoFillHandler = () => {
    if (uploadRef.current) {
      uploadRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div ref={formRef}>
          <div className="$font-body-regular " style={{ display: "flex" }}>
            <Notification
              text=""
              title={
                <span>
                  This Close Out form can be automatically filled by uploading
                  relevant documents (SOW recommended)in supported formats (
                  .DOCX , .PDF ).At least one uploaded document must be the
                  Statement of Work (SOW).
                  <Link
                    className="$color-background-layer-01"
                    onClick={autoFillHandler}
                    disabled={false}
                    label="Click here"
                    size="medium"
                    style={{
                      color: "#316bb8",
                      fontWeight: "bold",
                      textDecoration: "underline",
                    }}
                    useAnchor
                  />{" "}
                  to use this feature.
                </span>
              }
              type={NotificationType.INFO}
            />
          </div>
          <IntakeFormFields
            formData={IntakeFormData}
            formattedProjectId={formattedProjectId}
            handleChange={historicalHandleChange}
            handleFileChange={historicalHandleFileChange}
            handleClose={handleCloseHistoricalDialog}
            errors={errors}
            emailErrors={emailErrors}
            inputError={validInputError}
            onFocus={handleFocus}
            onBlur={handleBlur}
            mode={isEditMode ? "edit" : "create"}
          >
            <div ref={uploadRef}>
              <DocumentUploadInput isedit={false} />
            </div>
          </IntakeFormFields>
        </div>
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              marginTop: "15px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                iconLeading="arrow_back_ios"
                size="medium"
                text="Back"
                variant="tertiary"
                onClick={handleCloseHistoricalDialog}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Button
                size="medium"
                text="Cancel"
                variant="secondary"
                onClick={handleCloseHistoricalDialog}
              />
              <Tooltip
                text="Restricted or unsupported file formats are not eligible for AutoFill."
                direction="top-middle"
              >
                <Button
                  size="medium"
                  text="Auto-fill"
                  variant="auxiliary"
                  onClick={() => {
                    setisModalOpen(true);
                  }}
                  disabled={isAutoFillDisabled}
                />
              </Tooltip>
              <Button
                size="medium"
                text="Submit"
                variant="primary"
                onClick={() => setOpenModal(true)}
                disabled={Loading || isSaveDisabled}
              />
            </div>
          </div>
        </div>
        <Backdrop
          open={Loading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <LoadingSpinner
            size={{
              height: "60px",
              width: "60px",
            }}
          />
          <div className="$font-heading-h6-medium" style={{ color: "#fff" }}>
            {isUploading ? "Uploading the files..." : "Submitting the form..."}
          </div>
        </Backdrop>
        <ModalPopUp
          open={openModal}
          handleClose={() => setOpenModal(false)}
          handleOnSubmit={submitIntakeRequest}
        />
        <AppToaster />
        <Backdrop
          open={loader}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <LoadingSpinner
            size={{
              height: "60px",
              width: "60px",
            }}
          />
          <div className="$font-heading-h6-medium" style={{ color: "#fff" }}>
            {isUploading
              ? "Uploading the files..."
              : "Analyzing Documents for Auto-fill..."}
          </div>
        </Backdrop>
        <ModalPopUp
          open={ismodalOpen}
          handleClose={() => {
            setisModalOpen(false);
          }}
          handleOnSubmit={HandleSubmit}
          isEdit={false}
        />
      </div>
    </>
  );
};
export default RequestForm;
